import React from "react";
import { Checkbox, Modal, Space } from "antd";
import { useTranslation } from "react-i18next";
import { getPermissionStructure } from "../../../../../utils/permissionUtils";

const EDIT_ENABLED_KEYS = [
  "shop-products",
  "shop-products-productList",
  "shop-products-addProduct",
  "shop-products-editProduct",
  "shop-products-stock",
  "shop-products-forecast",
];

const PermissionsModal = ({
  visible,
  onCancel,
  onOk,
  editablePermissions,
  handlePermissionChange,
  permissionsLoading,
}) => {
  const { t } = useTranslation();
  const permissionStructure = getPermissionStructure();

  // Get all permission keys that support edit
  const getAllPermissionKeys = () => {
    const keys = [];
    permissionStructure.forEach((group) => {
      if (group.hasPath) {
        keys.push(group.key);
      }
      group.children.forEach((child) => {
        keys.push(child.key);
      });
    });
    return keys;
  };

  // Check if all view permissions are selected
  const areAllViewSelected = () => {
    const allKeys = getAllPermissionKeys();
    return allKeys.every((key) => editablePermissions[key]?.view === true);
  };

  // Check if all edit permissions are selected (only EDIT_ENABLED_KEYS)
  const areAllEditSelected = () => {
    return EDIT_ENABLED_KEYS.every((key) => editablePermissions[key]?.edit === true);
  };

  // Handle select/unselect all for view permissions
  const handleToggleAllView = (checked) => {
    const allKeys = getAllPermissionKeys();
    allKeys.forEach((key) => {
      handlePermissionChange(key, "view", checked);
    });
  };

  // Handle select/unselect all for edit permissions (only EDIT_ENABLED_KEYS)
  const handleToggleAllEdit = (checked) => {
    EDIT_ENABLED_KEYS.forEach((key) => {
      handlePermissionChange(key, "edit", checked);
    });
  };

  return (
    <Modal
      title={t("update_permissions")}
      open={visible}
      onOk={onOk}
      onCancel={onCancel}
      okText={t("save_permissions")}
      cancelText={t("cancel")}
      confirmLoading={permissionsLoading}
      width={700}
      zIndex={1001}
    >
      <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
        <Space size="large">
          <Checkbox checked={areAllViewSelected()} onChange={(e) => handleToggleAllView(e.target.checked)}>
            <strong>{t("select_all_view")}</strong>
          </Checkbox>
          <Checkbox checked={areAllEditSelected()} onChange={(e) => handleToggleAllEdit(e.target.checked)}>
            <strong>{t("select_all_edit")}</strong>
          </Checkbox>
        </Space>
      </div>
      <div style={{ maxHeight: "500px", overflowY: "auto" }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          {permissionStructure.map((group) => (
            <div key={group.key} style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "14px",
                  marginBottom: "8px",
                  backgroundColor: "rgba(0,0,0,0.02)",
                  padding: "8px",
                }}
              >
                {t(group.title)}
              </div>

              {group.hasPath && (
                <div
                  style={{
                    padding: "8px 0 8px 16px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <Space>
                    <Checkbox
                      checked={editablePermissions[group.key]?.view}
                      onChange={(e) => handlePermissionChange(group.key, "view", e.target.checked)}
                    >
                      {t("view")}
                    </Checkbox>
                    <Checkbox
                      checked={editablePermissions[group.key]?.edit}
                      onChange={(e) => handlePermissionChange(group.key, "edit", e.target.checked)}
                      disabled={!EDIT_ENABLED_KEYS.includes(group.key)}
                    >
                      {t("edit")}
                    </Checkbox>
                  </Space>
                </div>
              )}

              {group.children.map((child) => {
                const permission = editablePermissions[child.key] || { view: false, edit: false };
                return (
                  <div
                    key={child.key}
                    style={{
                      padding: "8px 0 8px 32px",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <div style={{ marginBottom: "4px" }}>{t(child.title)}</div>
                    <Space>
                      <Checkbox
                        checked={permission.view}
                        onChange={(e) => handlePermissionChange(child.key, "view", e.target.checked)}
                      >
                        {t("view")}
                      </Checkbox>
                      <Checkbox
                        checked={permission.edit}
                        onChange={(e) => handlePermissionChange(child.key, "edit", e.target.checked)}
                        disabled={!EDIT_ENABLED_KEYS.includes(child.key)}
                      >
                        {t("edit")}
                      </Checkbox>
                    </Space>
                  </div>
                );
              })}
            </div>
          ))}
        </Space>
      </div>
    </Modal>
  );
};

export default PermissionsModal;
